// Florida Roof Certificate landing page
// Domain target: roofcertificateflorida.com
// Single-purpose: educate FL homeowners on roof certificates required for
// homeowners insurance renewal, and capture registrations that route to
// O'Brien Contracting via the Growth Hub lead pipeline.

import { useState } from "react";
import DomainSEO from "@/components/seo/DomainSEO";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ShieldCheck,
  FileCheck2,
  Home,
  Phone,
  Clock,
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  ArrowRight,
} from "lucide-react";
import { COMPANY, PUBLIC_EMAIL } from "@/lib/constants";
import { sendLeadToGrowthHub } from "@/lib/sendLeadToGrowthHub";
import CarrierFinder from "@/components/rcf/CarrierFinder";
import { rcfEntityGraph } from "@/lib/rcfEntitySchema";
import heroFloridaHome from "@/assets/florida-home-new-roof.jpg";
import oldRoofImg from "@/assets/florida-old-roof.jpg";
import damagedRoofImg from "@/assets/florida-damaged-roof.jpg";
import newRoofInstallImg from "@/assets/florida-new-roof-install.jpg";
import inspectorImg from "@/assets/florida-roof-inspector.jpg";
import tileHomeImg from "@/assets/florida-tile-home.jpg";

const CANONICAL = "https://roofcertificateflorida.com/";

export const FAQS = [
  {
    q: "What is a Florida roof certificate?",
    a: "A Florida roof certificate (often called a roof condition certification, Form OIR-B1-1802, or 4-point inspection roof section) is a standardized report completed by a licensed contractor or inspector. It documents the roof's age, material, condition, and remaining useful life so your insurance carrier can confirm it meets underwriting requirements.",
  },
  {
    q: "Why do insurance companies require it now?",
    a: "Florida's property insurance market has tightened. Most carriers (Citizens, Universal, Heritage, Tower Hill, and others) now require a current roof certificate before issuing or renewing a homeowners policy — especially if your roof is more than 10–15 years old.",
  },
  {
    q: "How long is a roof certificate valid?",
    a: "Most insurers accept roof certificates dated within the last 12 months. Some carriers require a fresh certificate every renewal cycle if the roof is older than 15 years.",
  },
  {
    q: "What does the inspection check?",
    a: "Roof covering type and age, visible damage, fastener condition, flashing and sealants, deck condition where visible, signs of leaks, and estimated remaining useful life. If wind mitigation features are present, those are documented on Form OIR-B1-1802.",
  },
  {
    q: "What if my roof fails the inspection?",
    a: "You'll receive a written list of items needed to pass — anything from minor repairs to a full replacement. O'Brien Contracting can quote the repairs, complete the work, and re-issue the certification so you stay insured.",
  },
  {
    q: "How much does a roof certificate cost?",
    a: "Standard certifications start at $0 when bundled with a free roof estimate from O'Brien Contracting. Stand-alone insurance certifications are typically $125–$250 depending on roof size and access.",
  },
];

const CARRIERS = [
  "Citizens Property Insurance",
  "Universal Property",
  "Heritage Insurance",
  "Tower Hill",
  "American Integrity",
  "Florida Peninsula",
  "Kin Insurance",
  "Slide Insurance",
];

const RoofCertificateFlorida = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zip: "",
    roofAge: "",
    insurer: "",
    notes: "",
  });

  const update = (key: keyof typeof form, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address) {
      toast({
        title: "Missing required information",
        description: "Please provide your name, phone, and property address.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);

    const notes = [
      `Roof age: ${form.roofAge || "unknown"}`,
      `Insurance carrier: ${form.insurer || "not provided"}`,
      form.notes,
    ]
      .filter(Boolean)
      .join(" | ");

    const leadPayload = {
      name: form.name,
      email: form.email || null,
      phone: form.phone,
      address: form.address,
      city: form.city || null,
      state: "FL",
      zip: form.zip || null,
      service_interest: "Florida Roof Certificate",
      notes,
      form_type: "roof_certificate_florida",
      priority: "high" as const,
    };

    const delivered = await sendLeadToGrowthHub(leadPayload);

    setSubmitting(false);
    if (!delivered) {
      toast({
        title: "Submission issue",
        description: "Please call us directly so we can schedule your inspection.",
        variant: "destructive",
      });
      return;
    }
    setSubmitted(true);
    toast({
      title: "Registration received",
      description: "O'Brien Contracting will call within one business day to schedule your inspection.",
    });
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Florida Roof Certificate Inspection",
    serviceType: "Roof Condition Certification for Homeowners Insurance",
    provider: {
      "@type": "RoofingContractor",
      name: COMPANY.name,
      telephone: COMPANY.phoneFormatted,
      url: "https://obriencontractingusa.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: COMPANY.address.street,
        addressLocality: COMPANY.address.city,
        addressRegion: COMPANY.address.state,
        postalCode: COMPANY.address.zip,
        addressCountry: "US",
      },
    },
    areaServed: { "@type": "State", name: "Florida" },
    description:
      "Certified Florida roof inspections and condition certifications required by homeowners insurance carriers for new policies and renewals.",
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DomainSEO
        title="Florida Roof Certificate for Insurance Renewal"
        description="Get the Florida roof certificate insurance carriers require for renewal. Statewide inspections by licensed contractor O'Brien Contracting. Register in 60 seconds."
        canonicalUrl={CANONICAL}
      >
        <script type="application/ld+json">{JSON.stringify(rcfEntityGraph())}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </DomainSEO>

      {/* Slim header */}
      <header className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-40">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 font-heading font-bold text-foreground">
            <ShieldCheck className="w-6 h-6 text-primary" />
            <span>RoofCertificateFlorida</span>
          </a>
          <a href={COMPANY.phoneHref}>
            <Button variant="outline" size="sm" className="gap-2">
              <Phone className="w-4 h-4" /> {COMPANY.phoneFormatted}
            </Button>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-primary text-primary-foreground py-16 md:py-24 overflow-hidden">
        {/* Background image + overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroFloridaHome}
            alt="Florida home with a certified roof under blue sky"
            className="w-full h-full object-cover opacity-40"
            width={1280}
            height={832}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/85 to-primary/70" />
        </div>
        <div className="relative z-10 container mx-auto px-4 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="inline-flex items-center gap-2 bg-accent/20 text-accent-foreground px-3 py-1 rounded-full text-sm font-medium mb-5">
              <AlertTriangle className="w-4 h-4" />
              Required by most FL insurers in 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-5">
              Get Your Florida Roof Certificate Before Your Insurance Renews
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/90 mb-8 leading-relaxed">
              Florida homeowners insurance carriers now require a current roof condition
              certification — typically every renewal cycle. Register your home and a licensed
              roofing contractor will inspect, document, and certify your roof so your policy
              stays active.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Accepted by Citizens, Universal, Heritage, Tower Hill & more",
                "Licensed & insured contractor — CCC1335947",
                "Statewide Florida coverage",
                "Free with a roof estimate when repairs are needed",
              ].map(item => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-primary-foreground/95">{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="#register">
                <Button size="lg" variant="accent" className="text-lg px-8 shadow-lg">
                  Register My Home <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href={COMPANY.phoneHref}>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                >
                  <Phone className="mr-2 w-5 h-5" /> Call Now
                </Button>
              </a>
            </div>
          </div>

          {/* Registration form */}
          <Card id="register" className="shadow-2xl border-0 scroll-mt-24">
            <CardHeader className="bg-card border-b border-border">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <ClipboardCheck className="w-5 h-5 text-primary" />
                Register for Your Roof Certificate
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Submitted to O'Brien Contracting — response within one business day.
              </p>
            </CardHeader>
            <CardContent className="p-6 bg-card">
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-14 h-14 text-accent mx-auto mb-4" />
                  <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                    Registration Received
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    A licensed inspector from O'Brien Contracting will contact you within one
                    business day to schedule your roof certification visit.
                  </p>
                  <a href={COMPANY.phoneHref}>
                    <Button variant="outline" className="gap-2">
                      <Phone className="w-4 h-4" /> Or call now: {COMPANY.phoneFormatted}
                    </Button>
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        required
                        value={form.name}
                        onChange={e => update("name", e.target.value)}
                        placeholder="Jane Smith"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        required
                        type="tel"
                        value={form.phone}
                        onChange={e => update("phone", e.target.value)}
                        placeholder="(239) 555-0100"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={e => update("email", e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Property Address *</Label>
                    <Input
                      id="address"
                      required
                      value={form.address}
                      onChange={e => update("address", e.target.value)}
                      placeholder="123 Palm St"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={form.city}
                        onChange={e => update("city", e.target.value)}
                        placeholder="Naples"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip">ZIP</Label>
                      <Input
                        id="zip"
                        value={form.zip}
                        onChange={e => update("zip", e.target.value)}
                        placeholder="34102"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="roofAge">Approx. Roof Age</Label>
                      <Input
                        id="roofAge"
                        value={form.roofAge}
                        onChange={e => update("roofAge", e.target.value)}
                        placeholder="e.g., 12 years"
                      />
                    </div>
                    <div>
                      <Label htmlFor="insurer">Insurance Carrier</Label>
                      <Input
                        id="insurer"
                        value={form.insurer}
                        onChange={e => update("insurer", e.target.value)}
                        placeholder="e.g., Citizens"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      rows={2}
                      value={form.notes}
                      onChange={e => update("notes", e.target.value)}
                      placeholder="Renewal date, known issues, etc."
                    />
                  </div>
                  <Button type="submit" size="lg" className="w-full" disabled={submitting}>
                    {submitting ? "Submitting..." : "Register My Roof Inspection"}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    By submitting, you agree to be contacted by O'Brien Contracting (CCC1335947)
                    about your roof certificate.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What it is */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3 text-center">
            What Is a Florida Roof Certificate?
          </h2>
          <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12">
            A licensed contractor inspects your roof and submits a standardized condition report
            to your insurance carrier. Without it, most Florida insurers will non-renew your
            policy.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: FileCheck2,
                title: "Condition Report",
                body: "Documents roof age, material, fastener condition, flashings, and remaining useful life on the carrier-accepted form.",
              },
              {
                icon: ShieldCheck,
                title: "Insurance Acceptance",
                body: "Issued on Form OIR-B1-1802 or carrier-specific equivalents accepted by Citizens, Universal, Heritage and others.",
              },
              {
                icon: Home,
                title: "Repair Pathway",
                body: "If your roof needs work to pass, you receive a written punch list and a repair quote — no obligation.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <Card key={title} className="border-border">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground text-lg mb-2">{title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Roof Condition Gallery */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3">
              What Florida Insurers Look For
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Roof age, weather damage, and material condition determine whether your policy gets
              renewed — or non-renewed. Here's what inspectors document on every certificate.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                img: oldRoofImg,
                tag: "Aged & Worn",
                tagColor: "bg-destructive/10 text-destructive",
                title: "Roofs 15+ Years Old",
                body: "Faded shingles, moss growth, and curling edges typically trigger non-renewal. Most carriers flag roofs over 15 years for full replacement.",
              },
              {
                img: damagedRoofImg,
                tag: "Storm Damaged",
                tagColor: "bg-accent/20 text-accent-foreground",
                title: "Wind & Hurricane Damage",
                body: "Lifted shingles, exposed underlayment, and missing tiles after Florida storms must be repaired before your insurance certificate can be issued.",
              },
              {
                img: newRoofInstallImg,
                tag: "Insurance Approved",
                tagColor: "bg-primary/10 text-primary",
                title: "New & Certified Roofs",
                body: "A newly installed or recently inspected roof in good condition earns a clean certificate — and often qualifies for wind mitigation discounts.",
              },
            ].map(item => (
              <Card key={item.title} className="overflow-hidden border-border group">
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img
                    src={item.img}
                    alt={item.title}
                    loading="lazy"
                    width={1024}
                    height={768}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <CardContent className="p-6">
                  <span
                    className={`inline-block text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full mb-3 ${item.tagColor}`}
                  >
                    {item.tag}
                  </span>
                  <h3 className="font-heading font-bold text-foreground text-lg mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* Process */}
      <section className="py-16 bg-muted/40">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl order-last lg:order-first">
              <img
                src={inspectorImg}
                alt="Licensed O'Brien Contracting roof inspector documenting a Florida home"
                loading="lazy"
                width={1024}
                height={768}
                className="w-full h-auto object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3">
                How It Works
              </h2>
              <p className="text-muted-foreground mb-8">
                A licensed O'Brien Contracting inspector handles every step — from scheduling
                through carrier-ready documentation.
              </p>
              <div className="space-y-5">
                {[
                  { step: "1", title: "Register Online", body: "Submit the form — takes under 60 seconds." },
                  { step: "2", title: "Schedule Inspection", body: "We call within one business day to set a time." },
                  { step: "3", title: "On-Site Certification", body: "Licensed inspector evaluates and documents your roof." },
                  { step: "4", title: "Send to Insurer", body: "Receive your certificate and forward to your carrier." },
                ].map(s => (
                  <div key={s.step} className="flex gap-4">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-primary text-primary-foreground font-heading font-bold flex items-center justify-center">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-foreground mb-1">{s.title}</h3>
                      <p className="text-sm text-muted-foreground">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Carriers */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2 text-center">
            Accepted by Florida's Major Insurance Carriers
          </h2>
          <p className="text-center text-muted-foreground mb-10">
            Our certifications meet the documentation requirements of:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CARRIERS.map(c => (
              <div
                key={c}
                className="border border-border rounded-lg px-4 py-3 text-center text-sm font-medium text-foreground bg-card"
              >
                {c}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Urgency */}
      <section className="relative py-20 text-white overflow-hidden bg-foreground">
        <div className="absolute inset-0">
          <img
            src={tileHomeImg}
            alt="Florida luxury home with terracotta tile roof"
            loading="lazy"
            width={1280}
            height={832}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/80 to-foreground/70" />
        </div>
        <div className="relative z-10 container mx-auto px-4 max-w-4xl text-center">
          <Clock className="w-10 h-10 mx-auto mb-3 text-accent" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-3">
            Renewal Coming Up? Don't Wait.
          </h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto leading-relaxed">
            Schedule your certification at least 30 days before your renewal date to allow time
            for any required repairs — and keep your Florida homeowners policy active.
          </p>
          <a href="#register">
            <Button size="lg" variant="accent" className="text-lg px-8 shadow-2xl">
              Register Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* Carrier Lookup */}
      <CarrierFinder />

      {/* FAQ */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-heading">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background py-10">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-3 gap-8 text-sm">
            <div>
              <div className="flex items-center gap-2 font-heading font-bold text-base mb-2">
                <ShieldCheck className="w-5 h-5" />
                RoofCertificateFlorida.com
              </div>
              <p className="text-background/70">
                A service of {COMPANY.name} — Florida State Certified Roofing Contractor
                {" "}({COMPANY.license}).
              </p>
            </div>
            <div>
              <div className="font-heading font-bold mb-2">Contact</div>
              <p className="text-background/70">
                <a href={COMPANY.phoneHref} className="hover:text-background">
                  {COMPANY.phoneFormatted}
                </a>
                <br />
                {PUBLIC_EMAIL && (
                  <a href={`mailto:${PUBLIC_EMAIL}`} className="hover:text-background">
                    {PUBLIC_EMAIL}
                  </a>
                )}
              </p>
            </div>
            <div>
              <div className="font-heading font-bold mb-2">Roofing Services</div>
              <p className="text-background/70">
                Need full roof repair or replacement? Visit{" "}
                <a
                  href="https://obriencontractingusa.com"
                  className="underline hover:text-background"
                >
                  obriencontractingusa.com
                </a>
              </p>
            </div>
          </div>
          <div className="border-t border-background/10 mt-8 pt-6 text-xs text-background/50 flex flex-wrap gap-4 justify-between">
            <div>© {new Date().getFullYear()} {COMPANY.name}. License {COMPANY.license}. All rights reserved.</div>
            <nav className="flex flex-wrap gap-4">
              <a href="/about" className="hover:text-background">About</a>
              <a href="/trust" className="hover:text-background">Trust &amp; Credentials</a>
              <a href="/editorial-policy" className="hover:text-background">Editorial Policy</a>
              <a href="/authors" className="hover:text-background">Authors</a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RoofCertificateFlorida;
